/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-catch-shadow */
import { googlePalService } from 'react-native-google-pal';
import { useQuery } from 'react-query';
import axios from 'axios';
import CryptoJS from 'crypto-js';

import { ADOBE_API_TOKEN_KEY, apiConfig, getPalRequest } from '@constants';
import { helpers } from '@helpers';

import { CustomError, FormatResponseVODData, ResponseVODData } from './types';

const getVODVideo = async (id: string, retry = false): Promise<CustomError | string | FormatResponseVODData> => {
  const { accessToken, error } = await helpers.getDspAccessToken(retry);

  const deviceInfo = helpers.isFireTV
    ? `{
    "model": "AFTM",
    "vendor": "Amazon",
    "manufacturer": "Amazon",
    "osName": "Android",
    "osVendor": "Google",
    "osVersion": "montoya",
    "browserVendor": "Google",
    "browserName": "Chrome",
    "applicationId": "weatherchannel"
  }`
    : helpers.isGoogleTV
    ? `{
    "model": "AFTM",
    "vendor": "Google",
    "manufacturer": "Google",
    "osName": "Android",
    "osVendor": "Google",
    "osVersion": "montoya",
    "browserVendor": "Google",
    "browserName": "Chrome",
    "applicationId": "weatherchannel"
  }`
    : helpers.isTizen
    ? `{
    "model": "AFTM",
    "vendor": "Samsung",
    "manufacturer": "Samsung",
    "osName": "Tizen",
    "osVendor": "Samsung",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`
    : helpers.isX1
    ? `{
    "model": "AFTM",
    "vendor": "X1",
    "manufacturer": "X1",
    "osName": "X1",
    "osVendor": "X1",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`
    : helpers.isTVOS
    ? `{
    "model": "AFTM",
    "vendor": "Apple",
    "manufacturer": "Apple",
    "osName": "TVOS",
    "osVendor": "Apple",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`
    : `{
    "model": "AFTM",
    "vendor": "Amazon",
    "manufacturer": "Amazon",
    "osName": "Android",
    "osVendor": "Google",
    "osVersion": "",
    "browserVendor": "",
    "browserName": "",
    "applicationId": "weatherchannel"
  }`;

  const deviceInfoString = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(deviceInfo));

  if (error) {
    return error;
  }

  const deviceID = helpers.getDeviceKey();
  const deviceType = helpers.platformName();

  const dataURL = helpers.replaceTemplateValues(apiConfig.VOD_VIDEO_STREAM_URL, {
    id,
    width: '1920',
    height: '1080',
  });
  const adobeToken = helpers.getStorageItem(ADOBE_API_TOKEN_KEY);

  const authZ = await axios
    .get(`${apiConfig.ADOBE_TVE_CHECKAUTHZ_URL}&deviceType=${deviceType}&deviceId=${deviceID}`, {
      headers: {
        Authorization: `Bearer ${adobeToken}`,
        'X-Device-Info': deviceInfoString,
      },
    })
    .catch(authZError => {
      console.log(`authZ error:, ${authZError}`);
    });

  console.log({ authZ });
  let serializedToken = '';

  await axios
    .get(`${apiConfig.ADOBE_SHORT_MEDIA_TOKEN}&deviceId=${deviceID}&deviceType=${deviceType}`, {
      headers: { Authorization: `Bearer ${adobeToken}`, 'X-Device-Info': deviceInfoString },
    })
    .then(SMTData => {
      serializedToken = SMTData?.data?.serializedToken;
    })
    .catch(smtError => {
      console.log('shortMediaToken error: ', smtError);
    });

  try {
    const { data } = await axios.get<ResponseVODData>(`${dataURL}?embedded_captions=true`, {
      headers: { 'x-access-token': accessToken, 'x-adobe-media-token': serializedToken },
    });
    const nonceResponse = await googlePalService.getNonceManager(getPalRequest('VOD'));

    if (nonceResponse.success) {
      const baseURL = helpers.getBaseURL(data.video_m3u8);
      const baseParams = helpers.getParamsFromUrl(data.video_m3u8);
      const resultUrl = helpers.attachParams(baseURL, { ...baseParams, 'ads.paln': nonceResponse.nonce });

      data.video_m3u8 = resultUrl;
    }
    return formatData(data);
  } catch (error) {
    return retry ? { errors: 'access error. Token expired?' } : getVODVideo(id, true);
  }
};

const formatData = (vodData: ResponseVODData) => ({
  id: vodData._id,
  url: vodData.video_m3u8,
  isLive: false,
  ...vodData,
});

/**
 * Pass in id of video to get video url and details from DSP
 */
export const useGetVODVideo = (id: string) => useQuery(`getVODVideo_${id}`, () => getVODVideo(id));
